import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Footer,
  footerItems,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  DataTable,
  FiltersBar,
  quoteFiltersConfig,
} from "@b2b/ui-components";
import { rfqService } from "@b2b/services";
import { RfqQuote } from "@b2b/services/api/modules/rfq/rfq.types";
import { Header } from "./components";
import { Eye, MessageSquare, Edit, History } from "lucide-react";
import { QuoteRevisionDialog } from "../RFQ_QuoteSubmission/components/QuoteRevisionDialog";

interface QuoteWithRfq extends RfqQuote {
  rfq?: {
    id: string;
    title: string;
    status: string;
  };
}

export default function RFQ_QuoteTablePage() {
  const [quotes, setQuotes] = useState<QuoteWithRfq[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<QuoteWithRfq | null>(null);

  useEffect(() => {
    fetchQuotes();
  }, [currentPage, statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getSellerQuotes({
        page: currentPage,
        limit: 20,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });

      if (response && response.items) {
        setQuotes(response.items);
        setTotalPages(response.totalPages || 1);
        setTotal(response.total || 0);
      }
    } catch (error: any) {
      console.error("Error fetching quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUBMITTED":
        return "bg-blue-500";
      case "ACCEPTED":
        return "bg-green-500";
      case "REJECTED":
        return "bg-red-500";
      case "WITHDRAWN":
        return "bg-gray-500";
      case "PENDING":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatDate = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
  };

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const tableData = quotes.map((quote) => ({
    id: quote.id,
    rfqTitle: quote.rfq?.title || "N/A",
    price: formatCurrency(quote.price, quote.currency),
    leadTimeDays: `${quote.leadTimeDays} days`,
    status: (
      <Badge className={getStatusColor(quote.status)}>{quote.status}</Badge>
    ),
    submittedDate: formatDate(quote.createdAt),
    rating: quote.rating ? `${quote.rating}/5` : "-",
    actions: (
      <div className="flex gap-1">
        {quote.rfq && quote.status === "SUBMITTED" && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => {
              setSelectedQuote(quote);
              setRevisionDialogOpen(true);
            }}
          >
            <Edit className="w-3 h-3 mr-1" />
            Revise
          </Button>
        )}
        {quote.rfq && (
          <Link to={`/rfq/${quote.rfq.id}`}>
            <Button variant="outline" size="sm" className="h-8 px-2">
              <Eye className="w-3 h-3" />
              View
            </Button>
          </Link>
        )}
        {quote.rfq && (
          <Link to={`/rfq/${quote.rfq.id}`}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MessageSquare className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>
    ),
  }));

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header Section */}
      <Header />

      {/* Filters Section */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="bg-white shadow rounded-xl p-4 sm:p-6 mb-6">
          <FiltersBar
            filters={[
              {
                type: "select",
                placeholder: "Status",
                value: statusFilter,
                options: [
                  { label: "All", value: "all" },
                  { label: "Submitted", value: "SUBMITTED" },
                  { label: "Accepted", value: "ACCEPTED" },
                  { label: "Rejected", value: "REJECTED" },
                  { label: "Withdrawn", value: "WITHDRAWN" },
                ],
                onChange: (value) => {
                  setStatusFilter(value as string);
                  setCurrentPage(1);
                },
              },
            ]}
          />
        </div>
      </div>

      {/* Quotes Table Section */}
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-12 flex-1">
        <Card className="bg-white shadow rounded-xl">
          <CardHeader>
            <CardTitle>My Submitted Quotes ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading quotes...</p>
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No quotes found.</p>
              </div>
            ) : (
              <>
                <DataTable
                  data={tableData}
                  config={{
                    columns: [
                      { key: "rfqTitle", label: "RFQ Title" },
                      { key: "price", label: "Price" },
                      { key: "leadTimeDays", label: "Lead Time" },
                      { key: "status", label: "Status" },
                      { key: "submittedDate", label: "Submitted" },
                      { key: "rating", label: "Rating" },
                      { key: "actions", label: "Actions" },
                    ],
                    styles: {
                      headerBg: "bg-gray-100",
                      headerText: "text-gray-700",
                      rowHover: "hover:bg-gray-50",
                    },
                  }}
                  rowsPerPage={20}
                />
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revision Dialog */}
      {selectedQuote && selectedQuote.rfq && (
        <QuoteRevisionDialog
          open={revisionDialogOpen}
          onOpenChange={setRevisionDialogOpen}
          rfqId={selectedQuote.rfq.id}
          quoteId={selectedQuote.id}
          currentPrice={selectedQuote.price}
          currentCurrency={selectedQuote.currency}
          currentLeadTime={selectedQuote.leadTimeDays}
          currentMessage={selectedQuote.message}
          onRevisionCreated={() => {
            fetchQuotes();
            setSelectedQuote(null);
          }}
        />
      )}
    </div>
  );
}
